// finance_dashboard_v1: หน้าหลักการเงิน — ภาพรวม dashboard 5 widgets
// - 3 cards: รายรับ/รายจ่าย/คงเหลือ (filter เดือนนี้ default + เทียบเดือนที่แล้ว)
// - กราฟ trend 12 เดือน (LineChart รายรับ + รายจ่าย เป็นยอดบาท)
// - แยกตามธุรกิจ (filter เดือน)
// - ยอดต่อบัญชี — สะสมทั้งหมด ignore filter, group ตาม business
// - รายการล่าสุด 5 รายการ
import React, { useState, useEffect } from 'react';
import API from '../api';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from 'recharts';

const S = '/api/finance/summary';
const money = function (n) { return Number(n || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); };
const card = { background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' };

// ===== Helpers =====
function ymd(d) { return d.getFullYear() + '-' + ('0' + (d.getMonth() + 1)).slice(-2) + '-' + ('0' + d.getDate()).slice(-2); }

function monthRange(offsetMonths) {
  // offsetMonths = 0 = เดือนนี้, -1 = เดือนที่แล้ว
  var now = new Date();
  var first = new Date(now.getFullYear(), now.getMonth() + offsetMonths, 1);
  var last = new Date(now.getFullYear(), now.getMonth() + offsetMonths + 1, 0);
  return { from: ymd(first), to: ymd(last) };
}

function buildQS(from, to) {
  var p = [];
  if (from) p.push('from=' + encodeURIComponent(from));
  if (to) p.push('to=' + encodeURIComponent(to + 'T23:59:59'));
  return p.length ? ('?' + p.join('&')) : '';
}

function pct(curr, prev) {
  if (!prev || prev === 0) return null;
  return ((curr - prev) / Math.abs(prev)) * 100;
}

function fmtPct(p) {
  if (p === null || !Number.isFinite(p)) return '—';
  var sign = p >= 0 ? '+' : '';
  return sign + p.toFixed(1) + '%';
}

function fmtMonth(ym) {
  // ym = '2026-05' → 'พ.ค.69'
  if (!ym) return '';
  var parts = ym.split('-');
  var months = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
  var m = parseInt(parts[1], 10) - 1;
  var yy = (parseInt(parts[0], 10) + 543) % 100;
  return months[m] + String(yy).padStart(2, '0');
}

function fmtDate(s) {
  if (!s) return '-';
  try {
    var d = new Date(s);
    return d.toLocaleDateString('th-TH', { day: '2-digit', month: '2-digit' }) + ' ' + d.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' });
  } catch (e) { return s; }
}

// ===== Date filter (เดือนนี้ / เดือนที่แล้ว / 3 เดือน / ปีนี้ / กำหนดเอง) =====
function DateFilter(props) {
  var presets = [
    { key: 'thismonth', label: 'เดือนนี้' },
    { key: 'lastmonth', label: 'เดือนที่แล้ว' },
    { key: '3m', label: '3 เดือน' },
    { key: 'year', label: 'ปีนี้' }
  ];
  var onPreset = function (key) {
    var now = new Date(), from = null, to = null;
    if (key === 'thismonth') { var r = monthRange(0); from = r.from; to = r.to; }
    else if (key === 'lastmonth') { var r2 = monthRange(-1); from = r2.from; to = r2.to; }
    else if (key === '3m') { var d = new Date(now.getFullYear(), now.getMonth() - 2, 1); from = ymd(d); to = ymd(now); }
    else if (key === 'year') { from = ymd(new Date(now.getFullYear(), 0, 1)); to = ymd(now); }
    props.onChange(from, to, key);
  };
  var bs = { padding: '6px 12px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#475569', cursor: 'pointer', fontSize: 12 };
  var bsActive = { padding: '6px 12px', borderRadius: 8, border: '2px solid #10b981', background: '#ecfdf5', color: '#10b981', cursor: 'pointer', fontSize: 12, fontWeight: 600 };
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
      <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>ช่วงวันที่:</span>
      {presets.map(function (p) {
        return <button key={p.key} onClick={function () { onPreset(p.key); }} style={props.activeKey === p.key ? bsActive : bs}>{p.label}</button>;
      })}
      <input type="date" value={props.from || ''} onChange={function (e) { props.onChange(e.target.value || null, props.to, 'custom'); }} style={{ padding: '6px 8px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }} />
      <span style={{ fontSize: 12, color: '#94a3b8' }}>ถึง</span>
      <input type="date" value={props.to || ''} onChange={function (e) { props.onChange(props.from, e.target.value || null, 'custom'); }} style={{ padding: '6px 8px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }} />
    </div>
  );
}

// ===== Summary cards (3 ใบ) =====
function SummaryCards(props) {
  var curr = props.current || { income: 0, expense: 0, balance: 0, count: 0 };
  var prev = props.previous || { income: 0, expense: 0, balance: 0, count: 0 };
  var items = [
    { label: 'รายรับ', value: curr.income, prevValue: prev.income, color: '#10b981', icon: '⬆️' },
    { label: 'รายจ่าย', value: curr.expense, prevValue: prev.expense, color: '#ef4444', icon: '⬇️' },
    { label: 'คงเหลือ', value: curr.balance, prevValue: prev.balance, color: (curr.balance >= 0 ? '#10b981' : '#ef4444'), icon: '💰' }
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 16 }}>
      {items.map(function (it, i) {
        var diff = pct(it.value, it.prevValue);
        var diffColor = diff === null ? '#94a3b8' : (diff >= 0 ? '#10b981' : '#ef4444');
        return (
          <div key={i} style={card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#64748b', marginBottom: 8 }}>
              <span>{it.icon}</span><span>{it.label}</span>
            </div>
            <div style={{ fontSize: 24, fontWeight: 700, color: it.color }}>{money(it.value)}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
              ก่อนหน้า {money(it.prevValue)} <span style={{ color: diffColor, fontWeight: 600 }}>{fmtPct(diff)}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ===== Trend chart (12 เดือน) =====
function TrendChart(props) {
  var data = (props.rows || []).map(function (r) { return { ym: fmtMonth(r.ym), รายรับ: r.income, รายจ่าย: r.expense }; });
  return (
    <div style={Object.assign({}, card, { marginBottom: 16 })}>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#0f172a' }}>📈 รายรับ-รายจ่าย ย้อนหลัง 12 เดือน</div>
      {data.length === 0 ? <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>ยังไม่มีข้อมูล</div>
        : (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="ym" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={function (v) { if (v >= 1000000) return (v / 1000000).toFixed(1) + 'M'; if (v >= 1000) return (v / 1000).toFixed(0) + 'k'; return v; }} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={function (v) { return money(v); }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="รายรับ" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              <Line type="monotone" dataKey="รายจ่าย" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        )
      }
    </div>
  );
}

// ===== Businesses (ตาม filter) =====
function BusinessesList(props) {
  var list = props.businesses || [];
  return (
    <div style={Object.assign({}, card, { marginBottom: 16 })}>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#0f172a' }}>🏢 แยกตามธุรกิจ</div>
      {list.length === 0 ? <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>ไม่มีข้อมูล</div>
        : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {list.map(function (b) {
              var color = b.balance >= 0 ? '#10b981' : '#ef4444';
              var sign = b.balance >= 0 ? '+' : '';
              return (
                <div key={b.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: '#f8fafc', borderRadius: 8 }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{b.name}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{b.count} รายการ · รับ {money(b.income)} · จ่าย {money(b.expense)}</div>
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: color }}>{sign}{money(b.balance)}</div>
                </div>
              );
            })}
          </div>
        )
      }
    </div>
  );
}

// ===== Account balances (สะสมทั้งหมด ignore filter) =====
function AccountBalances(props) {
  var groups = props.businesses || [];
  return (
    <div style={Object.assign({}, card, { marginBottom: 16 })}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>🏦 ยอดต่อบัญชี</div>
        <div style={{ fontSize: 11, color: '#94a3b8' }}>ยอดสะสมตามระบบ (ไม่ใช่ยอดในธนาคารจริง)</div>
      </div>
      {groups.length === 0 ? <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>ยังไม่มีบัญชี</div>
        : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {groups.map(function (g) {
              return (
                <div key={g.business_id}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>{g.business_name}</div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: (g.total_balance >= 0 ? '#10b981' : '#ef4444') }}>รวม {money(g.total_balance)}</div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {(g.accounts || []).map(function (a) {
                      var color = a.balance >= 0 ? '#0f172a' : '#ef4444';
                      return (
                        <div key={a.account_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: '#f8fafc', borderRadius: 6, fontSize: 13 }}>
                          <div>
                            <span style={{ fontWeight: 500 }}>{a.label}</span>
                            {a.bank_code && a.label !== a.bank_code && <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 8 }}>· {a.bank_code}</span>}
                            {a.account_number && <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 8 }}>· {a.account_number}</span>}
                          </div>
                          <div style={{ color: color, fontWeight: 600 }}>{money(a.balance)}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )
      }
    </div>
  );
}

// ===== Recent transactions (5 ล่าสุด) =====
function RecentTransactions(props) {
  var rows = props.rows || [];
  return (
    <div style={Object.assign({}, card, { marginBottom: 16 })}>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#0f172a' }}>📋 รายการล่าสุด 5 รายการ</div>
      {rows.length === 0 ? <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>ยังไม่มีรายการ</div>
        : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e2e8f0', textAlign: 'left', color: '#94a3b8', fontSize: 11 }}>
                  <th style={{ padding: '8px 6px', fontWeight: 500 }}>วันที่</th>
                  <th style={{ padding: '8px 6px', fontWeight: 500 }}>ธุรกิจ</th>
                  <th style={{ padding: '8px 6px', fontWeight: 500 }}>หมวด</th>
                  <th style={{ padding: '8px 6px', fontWeight: 500 }}>บัญชี</th>
                  <th style={{ padding: '8px 6px', fontWeight: 500, textAlign: 'right' }}>จำนวน</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(function (r) {
                  var isIncome = r.type === 'income';
                  var color = isIncome ? '#10b981' : '#ef4444';
                  var sign = isIncome ? '+' : '-';
                  return (
                    <tr key={r.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '10px 6px', color: '#64748b' }}>{fmtDate(r.occurred_at)}</td>
                      <td style={{ padding: '10px 6px' }}>{r.business_name}</td>
                      <td style={{ padding: '10px 6px', color: '#475569' }}>{r.category || '-'}</td>
                      <td style={{ padding: '10px 6px', color: '#94a3b8', fontSize: 12 }}>{r.account_label || '-'}</td>
                      <td style={{ padding: '10px 6px', textAlign: 'right', color: color, fontWeight: 600 }}>{sign}{money(r.amount)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      }
    </div>
  );
}

// ===== Main page =====
export default function BillFinanceDashboard() {
  // Filter state — เริ่มต้น "เดือนนี้"
  var initRange = monthRange(0);
  const [from, setFrom] = useState(initRange.from);
  const [to, setTo] = useState(initRange.to);
  const [activeKey, setActiveKey] = useState('thismonth');

  // Data states
  const [currentData, setCurrentData] = useState(null); // overview ตาม filter
  const [previousData, setPreviousData] = useState(null); // overview เดือนก่อนหน้า (สำหรับ %)
  const [trendData, setTrendData] = useState(null);
  const [accountsData, setAccountsData] = useState(null);
  const [recentData, setRecentData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load current + previous overview
  useEffect(function () {
    setLoading(true);
    var qs = buildQS(from, to);

    // คำนวณช่วงก่อนหน้า — ถ้าเป็นเดือนใดๆ shift -1 month
    // ถ้าเป็น "ปีนี้" / "3 เดือน" / "custom" → ใช้ length เดียวกัน shift กลับ
    var prevQs = '';
    if (from && to) {
      var fromD = new Date(from), toD = new Date(to);
      var diffMs = toD.getTime() - fromD.getTime();
      var prevTo = new Date(fromD.getTime() - 24 * 3600 * 1000);
      var prevFrom = new Date(prevTo.getTime() - diffMs);
      prevQs = buildQS(ymd(prevFrom), ymd(prevTo));
    }

    Promise.all([
      API.get(S + '/overview' + qs).then(function (r) { return r.data; }).catch(function () { return null; }),
      prevQs ? API.get(S + '/overview' + prevQs).then(function (r) { return r.data; }).catch(function () { return null; }) : Promise.resolve(null)
    ]).then(function (results) {
      setCurrentData(results[0]);
      setPreviousData(results[1]);
      setLoading(false);
    });
  }, [from, to]);

  // Load static widgets (trend, accounts, recent) — load once
  useEffect(function () {
    API.get(S + '/trend?months=12').then(function (r) { setTrendData(r.data); }).catch(function () { setTrendData(null); });
    API.get(S + '/account-balances').then(function (r) { setAccountsData(r.data); }).catch(function () { setAccountsData(null); });
    API.get(S + '/recent-transactions?limit=5').then(function (r) { setRecentData(r.data); }).catch(function () { setRecentData(null); });
  }, []);

  var onDateChange = function (f, t, key) { setFrom(f); setTo(t); setActiveKey(key); };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0, color: '#0f172a' }}>📊 หน้าหลักการเงิน</h1>
        <div style={{ fontSize: 12, color: '#94a3b8' }}>ภาพรวมรายรับ-รายจ่ายทุกธุรกิจ</div>
      </div>

      <DateFilter from={from} to={to} activeKey={activeKey} onChange={onDateChange} />

      {loading ? <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>กำลังโหลด...</div>
        : (
          <>
            <SummaryCards
              current={currentData && currentData.total}
              previous={previousData && previousData.total}
            />
            <TrendChart rows={trendData && trendData.rows} />
            <BusinessesList businesses={currentData && currentData.businesses} />
            <AccountBalances businesses={accountsData && accountsData.businesses} />
            <RecentTransactions rows={recentData && recentData.rows} />
          </>
        )
      }
    </div>
  );
}
